import { useEffect, useState } from 'react';

/*
 * 后台显示封面时镜像 App 的处理。
 *
 * App 里白线稿封面按主题色重新着色（MusicPlayerView.coverImage 走 .alwaysTemplate，
 * 前景色 Theme.brown = 132/86/89），彩色插画保留原色。判定依据是饱和度而不是白不白，
 * 见 mnrp07/Core/ResourceStore.swift 的 isMonochrome(image:)。
 *
 * 这里只改显示，绝不动上传的字节。把棕色烤进文件会适得其反：#845659 的通道极差是 46，
 * 超过客户端 30 的阈值，那张图会被判成彩色插画，从此不再跟主题色走，
 * 锁屏封面也不再铺奶油底——等于把客户端做得更好的事关掉。
 */

/** 与 Theme.brown 和 styles.css 的 --ink 同值 */
export const TINT = '#845659';

/* 三个常数照抄 ResourceStore.isMonochrome，改这里就等于和客户端脱钩 */
const ALPHA_FLOOR = 60;
const SATURATION_LIMIT = 30;
const SAMPLE_SIDE = 64;

export type CoverInfo = {
  lineArt: boolean;
  /** 仅线稿有：染成主题棕之后的 data URL */
  tinted?: string;
};

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('This file could not be read as an image'));
    img.src = src;
  });
}

function draw(img: HTMLImageElement, w: number, h: number) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('This browser does not provide a 2D canvas');
  ctx.drawImage(img, 0, 0, w, h);
  return { canvas, ctx };
}

/**
 * 抽样判断是不是单色线稿。
 *
 * 客户端那份要先还原预乘 alpha，这里不用：canvas 的 getImageData 给的就是非预乘值。
 */
function isLineArt(img: HTMLImageElement) {
  const scale = Math.min(1, SAMPLE_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const { ctx } = draw(img, w, h);

  const px = ctx.getImageData(0, 0, w, h).data;
  let opaque = 0;
  let colored = 0;
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3] <= ALPHA_FLOOR) continue;
    opaque += 1;
    const hi = Math.max(px[i], px[i + 1], px[i + 2]);
    const lo = Math.min(px[i], px[i + 1], px[i + 2]);
    if (hi - lo > SATURATION_LIMIT) colored += 1;
  }
  return opaque > 0 && colored * 10 < opaque;
}

/** 等价于 UIKit 的 .alwaysTemplate：只留 alpha，颜色一律换成主题棕 */
function tint(img: HTMLImageElement) {
  const { canvas, ctx } = draw(img, img.naturalWidth, img.naturalHeight);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = TINT;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

export async function inspectImage(src: string): Promise<CoverInfo> {
  const img = await loadImage(src);
  try {
    return isLineArt(img) ? { lineArt: true, tinted: tint(img) } : { lineArt: false };
  } catch {
    // 本地开发时封面是从 CDN 跨域取的，canvas 被污染读不了像素。
    // 线上后台和封面同源不会走到这里，退回原图显示即可
    return { lineArt: false };
  }
}

/* 签名参数每 45 分钟换一次，缓存按路径存；缓存里也只存"是不是线稿"和染色结果，
   不存签名 URL 本身，否则续签之后拿出来的会是过期地址 */
const cache = new Map<string, Promise<CoverInfo>>();
const cacheKey = (url: string) => url.split('?')[0];

/** 返回该显示的地址：线稿给染色版，彩色插画原样返回 */
export function useCover(url: string | null | undefined): string | null {
  const key = url ? cacheKey(url) : '';
  const [info, setInfo] = useState<CoverInfo | null>(null);

  useEffect(() => {
    if (!url) {
      setInfo(null);
      return;
    }
    let alive = true;
    let hit = cache.get(key);
    if (!hit) {
      hit = inspectImage(url);
      cache.set(key, hit);
    }
    hit.then((result) => { if (alive) setInfo(result); })
      .catch(() => { cache.delete(key); });
    return () => { alive = false; };
    // url 只有签名参数在变，按路径依赖，避免每次续签都重算一遍
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!url) return null;
  // 还在判定时先显示原图：这一步通常只有几毫秒，留白反而更晃眼
  return info?.lineArt ? info.tinted! : url;
}
